<template>
  <LmDialog v-model="dialog" :styles="{ minWidth: '500px', minHeight: '200px' }" :title="modalTitle">
    <template #toolbar-actions>
      <q-btn color="tertiary" size="sm" class="q-mr-sm" @click="createStateClicked">
        {{ btnText }}
      </q-btn>
    </template>

    <template v-if="dialog && board" #content>
      <SpinnerAudioLoader :loading="loading" />
      <div class="dialog-content">
        <q-input
          ref="name"
          v-model.trim="name"
          :rules="[val => !!val || 'Required']"
          label="Name *"
          outlined
          autofocus
          @keydown.enter="createStateClicked"
        />

        <q-input
          v-model.number="order"
          :error="!isOrderValid"
          :label="`Order (min:1, max: ${board.states.length + 1})`"
          error-message="Please enter valid order"
          type="number"
          outlined
          no-error-icon
          @keydown.enter="createStateClicked"
        />
      </div>
    </template>
  </LmDialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { cloneDeep } from 'lodash'
import { numberBetween } from '@/lib/generic.utils'
import * as apiTo from '../api'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import SpinnerAudioLoader from '@/reusable/base-components/SpinnerAudioLoader'

export default {
  components: {
    LmDialog,
    SpinnerAudioLoader
  },

  props: {
    value: Boolean,
    editItem: Object,
    editItemOrder: Number
  },

  data: () => ({
    name: '',
    order: 1,
    isOrderValid: true,
    loading: false
  }),

  computed: {
    ...mapGetters('kanban-board', ['board']),

    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    modalTitle() {
      if (this.editItem) return 'Update State'
      return 'Create State'
    },

    btnText() {
      if (this.editItem) return 'Update'
      return 'Create'
    }
  },

  watch: {
    order(val) {
      this.isOrderValid = !(val < 1 || val > this.board.states.length + 1 || !val)
    },

    value(val) {
      val && this.editItem && this.setFields({ ...this.editItem, order: this.editItemOrder })
    }
  },

  methods: {
    ...mapMutations('kanban-board', ['addState', 'updateState']),

    setFields(fields) {
      this.name = fields.name
      this.order = fields.order
    },

    createStateClicked() {
      this.$refs.name.validate()
      if (this.$refs.name.hasError || !this.isOrderValid) return
      const states = cloneDeep(this.board.states)
      const order = this.order - 1
      let pos = 65535

      if (order === 0) pos = numberBetween(0, states[0].kanban_position)
      else if (order === states.length) pos += states[states.length - 1].kanban_position
      else {
        const prevPos = states[order - 1].kanban_position
        const nextPos = states[order].kanban_position
        pos = numberBetween(prevPos, nextPos)
      }

      const payload = {
        name: this.name,
        kanban_position: pos,
        board_id: this.$route.params.id
      }

      if (this.editItem) this.editState(payload)
      else this.createState(payload)
    },

    createState(payload) {
      this.loading = true
      apiTo
        .createState(payload)
        .then(({ data }) => {
          this.dialog = false
          this.addState(data.result)
          Object.assign(this.$data, this.$options.data.apply(this))
        })
        .finally(() => (this.loading = false))
    },

    editState(payload) {
      this.loading = true
      apiTo
        .updateState(this.editItem.id, payload)
        .then(({ data }) => {
          this.dialog = false
          this.updateState(data.result)
          Object.assign(this.$data, this.$options.data.apply(this))
        })
        .finally(() => (this.loading = false))
    }
  }
}
</script>

<style lang="stylus" scoped>
.dialog-content {
  display grid
  grid-auto-flow column
  align-items center
  gap 10px
  padding 15px
}
</style>
